import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  ProjectUser,
  ProjectUserFilterParams,
  ProjectUserListResponse,
} from '@/types/systemprojectuser.types';

import { projectSystemUserService } from '@/services/adminUserService/projectUserSerice';

interface ProjectSystemUserState {
  data: ProjectUser[];
  meta: ProjectUserListResponse['meta'];
  loading: boolean;
}

const initialState: ProjectSystemUserState = {
  data: [],
  meta: {
    totalPages: 0,
    currentPage: 0,
    pageSize: 0,
    totalCount: 0,
  },
  loading: false,
};

export const fetchProjectUsers = createAsyncThunk(
  'projectSystemUsers/fetch',
  async (params: ProjectUserFilterParams) => {
    return await projectSystemUserService.getAll(params);
  },
);

export const deleteProjectUser = createAsyncThunk(
  'projectSystemUsers/delete',
  async (id: number) => {
    await projectSystemUserService.remove(id);
    return id;
  },
);

export const updateProjectUserStatus = createAsyncThunk(
  'projects/update',
  async (payload: { id: number; status: boolean }) => {
    const { id, status } = payload;
    await projectSystemUserService.update(id, { status });
    return payload;
  },
);

export const toggleProjectUserStatus = createAsyncThunk(
  'projectSystemUsers/toggleStatus',
  async (id: number) => {
    const response = await projectSystemUserService.toggleStatus(id);
    return response.data;
  },
);

const projectSystemUserSlice = createSlice({
  name: 'projectSystemUsers',
  initialState,
  reducers: {
    updateProjectUserInList: (state, action) => {
      const { id, payload } = action.payload;
      const user = state.data.find((u) => u.id === id);
      if (user) {
        Object.assign(user, payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjectUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProjectUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data.map((u) => ({
          ...u,
          status: u.project?.status ?? false,
        }));
        state.meta = action.payload.meta;
      })

      .addCase(fetchProjectUsers.rejected, (state) => {
        state.loading = false;
      })
      .addCase(deleteProjectUser.fulfilled, (state, action) => {
        state.data = state.data.filter((u) => u.id !== action.payload);
      })
      .addCase(updateProjectUserStatus.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        const user = state.data.find((u) => u.id === id);
        if (user) {
          user.status = status;
        }
      })
      .addCase(toggleProjectUserStatus.fulfilled, (state, action) => {
        const user = state.data.find((u) => u.id === action.payload.id);
        if (user) {
          user.status = action.payload.status;
        }
      });
  },
});

export const { updateProjectUserInList } = projectSystemUserSlice.actions;
export default projectSystemUserSlice.reducer;
